import { useMemo } from 'react';
import {
  useProductByHandle,
  useProductInventoryByHandle,
} from '@backpackjs/storefront';
import { Box, Button, Paragraph } from 'theme-ui';

import { Picture } from '@snippets';

import { themed } from './ColorOptions.theme';

export const ColorOption = themed(
  ({
    theme,
    handle,
    selectedColor,
    setSelectedOptions,
    isGrouped = false,
    inventory: initialInventory = {},
  }) => {
    const { product } = useProductByHandle({ handle });

    const color = useMemo(() => {
      if (!product) return null;
      return product?.tags
        ?.find((tag) => tag.match(/color::/gi))
        ?.split('::')[1]
        ?.trim();
    }, [product?.id]);

    const firstImage = useMemo(() => {
      return product?.media?.find((item) => {
        return item.mediaContentType === 'IMAGE';
      })?.image;
    }, [product?.id]);

    const isSelected = color === selectedColor;

    const { inventory, ...inventoryStatus } = initialInventory?.id
      ? { inventory: initialInventory, finished: true }
      : useProductInventoryByHandle({
          handle: product?.handle,
          withQuantity: true,
        });

    const isGiftCard =
      inventory?.handle === 'brumate-e-gift-card' ||
      product?.handle === 'brumate-e-gift-card';

    const isSoldOut = useMemo(() => {
      if (!inventoryStatus?.finished) return false;
      return !inventory || (!inventory.availableForSale && !isGiftCard);
    }, [inventory, inventoryStatus]);

    const selectedStyles = useMemo(() => {
      if (isSoldOut && isGrouped) {
        return isSelected
          ? theme.value.unavailable.selected
          : theme.value.unavailable.unselected;
      }
      return isSelected
        ? theme.value.available.selected
        : theme.value.available.unselected;
    }, [isSelected, isSoldOut, isGrouped]);

    return (
      <Box
        as="li"
        data-comp={ColorOption.displayName}
        sx={theme.colorOptionWrapper}
      >
        <Button
          aria-label={color}
          aria-selected={isSelected}
          role="option"
          variant="buttons.plain"
          onClick={() => {
            setSelectedOptions((prev) => ({ ...prev, color }));
          }}
          sx={{
            ...theme.value,
            ...(selectedStyles || {}),
          }}
          style={
            isSoldOut && isGrouped
              ? {
                  background:
                    'linear-gradient(to top left, transparent 0%, transparent calc(50% - 1px), currentColor 50%, transparent calc(50% + 1px), transparent 100%)',
                }
              : {}
          }
        >
          <Picture
            alt={color}
            width={130}
            height={130}
            images={[
              {
                src: firstImage?.originalSrc,
                ratio: 1,
                width: 54,
              },
            ]}
            sx={{
              opacity: isSoldOut && !isSelected && isGrouped ? 0.4 : 1,
              zIndex: isSoldOut && isGrouped ? -1 : 0,
            }}
          />
        </Button>
        {color && <Paragraph sx={theme.colorName}>{color}</Paragraph>}
      </Box>
    );
  }
);

ColorOption.displayName = 'ColorOption';
